import { IDropdownOption, MessageBarType } from '@fluentui/react';
import { Button, buttonStylesGhost, buttonStylesPrimary, Dropdown, MessageBar, Panel, TextField } from '@h2oai/ui-kit';
import React, { useCallback, useState } from 'react';

import { EgressUpdateRequest } from '../../../../../admin-center/gen/fw_management/fw_management_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { buildUserContext } from '../../../common/utils';
import { protocolKey, protocolType } from '../../../constants';
import { firewallInfoMessageStyles, sidePanelFooterStyle } from '../FWManagement.styles';
import { ParsedResponseMessages, parseResponseMessage } from '../FWManagement.utils';

interface PortPanelProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmitSuccess: () => Promise<void>;
}

export default function PortSidePanel({ isOpen, setOpen, onSubmitSuccess }: PortPanelProps) {
  const service = useAdminCenterService();
  const user = useUser();
  const [portValue, setPortValue] = useState('');
  const [parsedMessages, setParsedMessages] = useState<ParsedResponseMessages | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const handlePortChange = (_event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setPortValue(newValue || '');
  };

  const handlePortFocus = () => {
    setParsedMessages(null);
    setErrorMessage(null);
  };

  const [selectedProtocol, setSelectedProtocol] = useState(protocolKey.tcp);
  const onProtocolChange = (_event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (!item) return;
    const { key } = item;
    setSelectedProtocol(key as protocolKey);
  };
  const portOptions = [
    { key: protocolKey.tcp, text: protocolType.tcp },
    { key: protocolKey.udp, text: protocolType.udp },
  ];

  const dismissPanel = useCallback(() => {
    setOpen(false);
    setParsedMessages(null);
    setErrorMessage(null);
  }, [setOpen]);
  const isFormValid = useCallback(() => {
    return selectedProtocol && portValue !== null && portValue !== undefined && portValue !== '';
  }, [selectedProtocol, portValue]);

  const handleSubmitClick = useCallback(async () => {
    if (!isFormValid()) return;

    const portList: string[] =
      portValue && selectedProtocol
        ? portValue
            .split(',')
            .map((port) => port.trim())
            .filter((port) => port !== '')
            .map((port) => `${port}-${selectedProtocol}`)
        : [];

    try {
      const userContext = await buildUserContext(user);
      const request: EgressUpdateRequest = {
        newFwPorts: portList,
        modifiedBy: user?.name ?? '',
        timestamp: Date.now() / 1000,
        userContext: userContext,
      };
      const response = await service.updateEgressRules(request);
      if (response.statusUpdate) {
        const parsed = parseResponseMessage(response.statusUpdate);
        if (parsed) {
          setParsedMessages(parsed);
          setErrorMessage(null);
        } else {
          // Fallback to showing the raw message if parsing fails
          setErrorMessage(response.statusUpdate);
          setParsedMessages(null);
        }
      } else {
        setErrorMessage('Error: Failed to update');
        setParsedMessages(null);
      }
      await onSubmitSuccess();
    } catch (error) {
      const errorMsg = error instanceof Error && error.message ? error.message : 'An unknown error occurred';
      setErrorMessage(`Error: ${errorMsg}`);
      setParsedMessages(null);
    }
  }, [portValue, onSubmitSuccess, isFormValid, user, selectedProtocol, service]);

  const onRenderFooterContent = useCallback(
    () => (
      <>
        <div style={sidePanelFooterStyle}>
          <Button styles={buttonStylesGhost} text="Cancel" onClick={dismissPanel} style={{ marginRight: 10 }} />
          <Button styles={buttonStylesPrimary} text="Add Port" onClick={handleSubmitClick} disabled={!isFormValid()} />
        </div>
      </>
    ),
    [dismissPanel, handleSubmitClick, isFormValid]
  );

  return (
    <Panel
      isLightDismiss
      isOpen={isOpen}
      onDismiss={dismissPanel}
      closeButtonAriaLabel="Close"
      isFooterAtBottom={true}
      onRenderFooterContent={onRenderFooterContent}
      customWidth="500px"
      type={7}
    >
      <h2>
        <strong>Add Outbound Port</strong>
      </h2>
      <h3>Enable outbound ports in your cloud environment</h3>
      <Dropdown
        label="Protocol"
        placeholder="Select protocol type"
        options={portOptions}
        selectedKey={selectedProtocol}
        onChange={onProtocolChange}
        required={true}
      />
      <TextField
        label="Outbound Port(s)"
        placeholder="Add comma separated ports. Ex: 8080, 443"
        value={portValue}
        onChange={handlePortChange}
        onFocus={handlePortFocus}
        required={true}
      />
      {parsedMessages && parsedMessages.added.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.success}>
          Successfully added: {parsedMessages.added.join(', ')}
        </MessageBar>
      )}
      {parsedMessages && parsedMessages.duplicates.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.warning}>
          Duplicates ignored: {parsedMessages.duplicates.join(', ')}
        </MessageBar>
      )}
      {parsedMessages && parsedMessages.invalid.length > 0 && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.error}>
          Invalid ignored: {parsedMessages.invalid.join(', ')}
        </MessageBar>
      )}
      {errorMessage && (
        <MessageBar styles={firewallInfoMessageStyles} messageBarType={MessageBarType.error}>
          {errorMessage}
        </MessageBar>
      )}
    </Panel>
  );
}
